import React, { useEffect, useState } from "react";
import AppLayout from "../../../components/layout/AppLayout";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { TEpic } from "../../../models/courses/course";
import { Button, Input, message, Select, Space, Table, Switch } from "antd";
import {
  SaveOutlined,
  EyeOutlined,
  EditOutlined,
  CopyOutlined,
  StopOutlined,
  DeleteOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useCourseApi } from "../../../api/courses/courses";
import { TLesson } from "../../../models/courses/lesson";
import type { ColumnsType } from "antd/es/table";
import { TCodification } from "../../../models/referential/referential";
import { useReferential } from "../../../context/ReferentialContext";
import {
  CODIFICATION_LESSON_TYPE,
  CODIFICATION_ROLES,
  CODIFICATION_TYPE,
} from "../../../constant/codification";
import ConfirmationDialog from "../../../components/layout/ConfirmationDialog";
import { useTilmiAuth } from "../../../hooks/useTilmiAuth";

const { TextArea } = Input;

const LessonList: React.FC = () => {
  const { t } = useTranslation();
  const { isRoleExist } = useTilmiAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    getLessonsByEpicId,
    updateLesson,
    createLesson,
    duplicateLesson,
    deleteLesson,
  } = useCourseApi();
  const [lessons, setLessons] = useState<TLesson[]>([]);
  const [isFreeFilter, setIsFreeFilter] = useState<'ALL' | 'FREE' | 'PAID'>('ALL');
  const [loading, setIsLoading] = useState<boolean>(false);
  const [isDuplicating, setIsDuplicating] = useState<boolean>(false);
  const [addingId, setAddingId] = useState<number>(0);
  const { getCodificationsByCodeType } = useReferential();
  const lessonTypes: TCodification[] = getCodificationsByCodeType(
    CODIFICATION_TYPE.LESSON_TYPE,
  );

  const epic: TEpic = location.state?.epic;

  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editingLesson, setEditingLesson] = useState<TLesson | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (!epic) {
      message.error("No epic selected");
      navigate("/setup/epic");
      return;
    }

    setIsLoading(true);
    getLessonsByEpicId(epic.id)
      .then((resp) => {
        setLessons(resp.data ?? []);
      })
      .catch((err) => {
        message.error(err.response.data ?? err.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
    // eslint-disable-next-line
  }, [epic]);

  const edit = (lesson: TLesson) => {
    setEditingKey(lesson.id);
    setEditingLesson({ ...lesson });
  };

  const handleAddLesson = () => {
    const idRow = addingId - 1;
    const newLesson: TLesson = {
      id: idRow,
      name: "",
      statement: "",
      lesson_order: 0,
      exercise_component: "",
      lesson_type_id: 0,
      epic_id: epic.id,
    };

    setAddingId(idRow);
    setEditingKey(idRow);
    setEditingLesson(newLesson);
    setLessons((prev) => [newLesson, ...(prev || [])]);
  };

  const save = (id: number) => {
    if (editingLesson) {
      (editingLesson.id > 0
        ? updateLesson(editingLesson.id, editingLesson)
        : createLesson(editingLesson)
      )
        .then((resp) => {
          const lessonResult: TLesson = resp.data;
          const updatedLessons = (lessons ?? []).map((lesson) =>
            lesson.id === id ? lessonResult : lesson,
          );
          setLessons(updatedLessons);
          message.success(t("message.savedSuccessfully"));
        })
        .catch((err) => {
          message.error(err.response.data ?? err.message);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }

    setEditingKey(null);
  };

  const duplicate = (id: number) => {
    setIsDuplicating(true);
    duplicateLesson(id)
      .then((resp) => {
        setLessons([...(lessons ?? []), resp.data]);
        message.success(t("message.duplicatedSuccessfully"));
      })
      .catch((err) => {
        message.error(err.response.data ?? err.message);
      })
      .finally(() => {
        setIsDuplicating(false);
      });
  };

  const confirmDelete = (id: number) => {
    setDeleteId(id);
    setIsDeleteDialogVisible(true);
  };

  const handleDelete = () => {
    if (deleteId == null) return;
    setDeleteLoading(true);
    deleteLesson(deleteId)
      .then(() => {
        setLessons((lessons ?? []).filter((lesson) => lesson.id !== deleteId));
        message.success(t("message.deletedSuccessfully"));
      })
      .catch((err) => {
        message.error(err.response.data ?? err.message);
      })
      .finally(() => {
        setDeleteLoading(false);
        setIsDeleteDialogVisible(false);
        setDeleteId(null);
      });
  };

  const cancel = () => {
    setEditingKey(null);
    setEditingLesson(null);
  };

  const showDetails = (lesson: TLesson) => {
    const codification = lessonTypes.find(
      (type) => type.id === lesson.lesson_type_id,
    );
    switch (codification?.code) {
      case CODIFICATION_LESSON_TYPE.BLOCKLY_PHASER:
        navigate("/setup/lesson-blockly-phaser", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.BLOCKLY_GAME:
        navigate("/setup/lesson-blockly-game", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.BLOCKLY_DRAW:
        navigate("/setup/lesson-blockly-draw", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.BLOCKLY_ARCADE:
        navigate("/setup/lesson-blockly-arcade", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_SLIDE:
        navigate("/setup/lesson-slide", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_FORMS:
        navigate("/setup/lesson-forms", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.PSEUDO_CODE:
        navigate("/setup/lesson-pscode", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.HTML_CSS:
        navigate("/setup/lesson-htmlcss", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.PYTHON_CODE:
        navigate("/setup/lesson-python", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.PYTHON_PHASER:
        navigate("/setup/lesson-pythonphaser", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.CONSOLE_JS:
        navigate("/setup/lesson-console-js", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.CONSOLE_PY:
        navigate("/setup/lesson-console-py", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_H5P:
        navigate("/setup/lesson-h5p", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_DATASET:
        navigate("/setup/lesson-ai-dataset", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_MODEL_BUILDER:
        navigate("/setup/lesson-ai-model-builder", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_PLAY_GROUND:
        navigate("/setup/lesson-ai-play-ground", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_PROPMTING:
        navigate("/setup/lesson-ai-prompting", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_PUZZLE:
        navigate("/setup/lesson-puzzle", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_ASSOCIATION:
        navigate("/setup/lesson-association", { state: { lesson, epic } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_GRAPHIC_DESIGN:
        navigate("/setup/lesson-graphic-design", { state: { lesson, epic } });
        break;

      default:
    }
  };

  const showTeacherView = (lesson: TLesson) => {
    const codification = lessonTypes.find(
      (type) => type.id === lesson.lesson_type_id,
    );
    switch (codification?.code) {
      case CODIFICATION_LESSON_TYPE.PSEUDO_CODE:
        navigate("/setup/lesson-pscode", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.PYTHON_PHASER:
        navigate("/setup/lesson-pythonphaser", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.BLOCKLY_PHASER:
        navigate("/setup/lesson-blockly-phaser", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_DATASET:
        navigate("/setup/lesson-ai-dataset", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_MODEL_BUILDER:
        navigate("/setup/lesson-ai-model-builder", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_PLAY_GROUND:
        navigate("/setup/lesson-ai-play-ground", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_AI_PROPMTING:
        navigate("/setup/lesson-ai-prompting", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_PUZZLE:
        navigate("/setup/lesson-puzzle", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_FORMS:
        navigate("/setup/lesson-forms", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_ASSOCIATION:
        navigate("/setup/lesson-association", { state: { lesson, epic, teacherMode: true } });
        break;
      case CODIFICATION_LESSON_TYPE.LESSON_GRAPHIC_DESIGN:
        navigate("/setup/lesson-graphic-design", { state: { lesson, epic, teacherMode: true } });
        break;
      default:
        break;
    }
  };

  const columns: ColumnsType<TLesson> = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: TLesson) =>
        editingKey === record.id ? (
          <Input
            value={editingLesson?.name}
            onChange={(e) => {
              if (editingLesson) {
                setEditingLesson({ ...editingLesson, name: e.target.value });
              }
            }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Gratuit",
      dataIndex: "is_free",
      key: "is_free",
      width: 120,
      render: (_: any, record: TLesson) =>
        editingKey === record.id ? (
          <Switch
            checked={editingLesson?.is_free ?? false}
            onChange={(checked: boolean) => {
              if (editingLesson) {
                setEditingLesson({ ...editingLesson, is_free: checked });
              }
            }}
            checkedChildren="Oui"
            unCheckedChildren="Non"
          />
        ) : (
          <Switch
            checked={record.is_free ?? false}
            disabled
            checkedChildren="Oui"
            unCheckedChildren="Non"
          />
        ),
    },
    {
      title: "Enoncé",
      dataIndex: "statement",
      key: "statement",
      render: (text: string, record: TLesson) =>
        editingKey === record.id ? (
          <TextArea
            rows={3}
            value={editingLesson?.statement}
            onChange={(e) => {
              if (editingLesson) {
                setEditingLesson({
                  ...editingLesson,
                  statement: e.target.value,
                });
              }
            }}
          />
        ) : (
          <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>
        ),
    },
    {
      title: "Ordre",
      dataIndex: "lesson_order",
      key: "lesson_order",
      render: (text: number, record: TLesson) =>
        editingKey === record.id ? (
          <Input
            type="number"
            value={editingLesson?.lesson_order}
            onChange={(e) => {
              if (editingLesson) {
                setEditingLesson({
                  ...editingLesson,
                  lesson_order: parseInt(e.target.value),
                });
              }
            }}
          />
        ) : (
          text
        ),
    },
    {
      title: "Type",
      dataIndex: "lesson_type_id",
      key: "lesson_type_id",
      render: (lessontypeid: number, record: TLesson) =>
        editingKey === record.id ? (
          <Select
            value={editingLesson?.lesson_type_id}
            onChange={(value: number) => {
              if (editingLesson) {
                setEditingLesson({ ...editingLesson, lesson_type_id: value });
              }
            }}
            style={{ width: "100%" }}
          >
            {lessonTypes.map((type: TCodification) => (
              <Select.Option key={type.id} value={type.id}>
                {type.label}
              </Select.Option>
            ))}
          </Select>
        ) : (
          lessonTypes.find((type) => type.id === lessontypeid)?.label || "N/A"
        ),
    },
    {
      title: "Action",
      key: "actions",
      fixed: "right" as "right",
      width: 220,
      render: (_: any, record: TLesson) => (
        <Space size="middle">
          {editingKey === record.id ? (
            <>
              <Button icon={<SaveOutlined />} onClick={() => save(record.id)} />
              <Button icon={<StopOutlined />} onClick={cancel} />
            </>
          ) : (
            <>
              <Button icon={<EditOutlined />} onClick={() => edit(record)} />
              <Button
                icon={<EyeOutlined />}
                onClick={() => showDetails(record)}
              />
              {(record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.PSEUDO_CODE)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.PYTHON_PHASER)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.BLOCKLY_PHASER)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_AI_DATASET)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_AI_MODEL_BUILDER)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_AI_PLAY_GROUND)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_AI_PROPMTING)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_PUZZLE)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_FORMS)?.id ||
                  record.lesson_type_id === lessonTypes.find((t) => t.code === CODIFICATION_LESSON_TYPE.LESSON_ASSOCIATION)?.id) && (
                <Button
                  icon={<UserOutlined />}
                  onClick={() => showTeacherView(record)}
                  title="Vue Enseignant"
                />
              )}
              <Button
                icon={<CopyOutlined />}
                onClick={() => duplicate(record.id)}
                loading={isDuplicating}
              />

              {isRoleExist(CODIFICATION_ROLES.DELETE_COURSE) && (
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => confirmDelete(record.id)}
                  loading={deleteId === record.id && deleteLoading}
                />
              )}
            </>
          )}
        </Space>
      ),
    },
  ];

    const handleBackNavigation = () => {
        navigate(-1);
    };

    return (
        <AppLayout 
            title={`Epic : ${epic?.name}`} 
            loading={loading}
            onBackNavigation={handleBackNavigation}
            showBackButton={true}
        >
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" onClick={handleAddLesson}>
          {t("button.add")}
        </Button>
        <Select
          value={isFreeFilter}
          style={{ width: 200 }}
          onChange={(v) => setIsFreeFilter(v)}
          options={[
            { label: 'Toutes les leçons', value: 'ALL' },
            { label: 'Leçons gratuites', value: 'FREE' },
            { label: 'Leçons non gratuites', value: 'PAID' },
          ]}
        />
      </Space>
      <Table<TLesson>
        columns={columns}
        dataSource={(lessons ?? []).filter(l => {
          if (isFreeFilter === 'ALL') return true;
          if (isFreeFilter === 'FREE') return l.is_free === true;
          return l.is_free === false || l.is_free === undefined; // undefined treated as non-free
        })}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        scroll={{ x: 800 }}
      />

      <ConfirmationDialog
        visible={isDeleteDialogVisible}
        onConfirm={handleDelete}
        onCancel={() => setIsDeleteDialogVisible(false)}
        loading={deleteLoading}
        title="Confirm Deletion"
        description="Are you sure you want to delete this lesson? This action is irreversible."
      />

    </AppLayout>
  );
};

export default LessonList;
